import type { Json } from '@/lib/database.types';
import { supabase } from '@/lib/supabase';
import {
  cellColorKey,
  normalizeCellColor,
  type CellColorChange,
} from './cellColorUtils';

export { cellColorKey, cellColorTargetsForRange, normalizeCellColor } from './cellColorUtils';
export type { CellColorChange, CellColorRange, CellColorTarget } from './cellColorUtils';

export async function loadCellColors(
  orgId: string,
  gridKey: string,
  documentId?: string | null,
): Promise<Map<string, string>> {
  const colors = new Map<string, string>();
  const pageSize = 1000;
  for (let offset = 0; ; offset += pageSize) {
    let query = supabase
      .from('cell_colors')
      .select('row_key, field, color')
      .eq('org_id', orgId)
      .eq('grid_key', gridKey);

    query = documentId
      ? query.eq('document_id', documentId)
      : query.is('document_id', null);

    const { data, error } = await query.order('field').order('row_key').range(offset, offset + pageSize - 1);
    if (error) throw error;

    for (const row of data ?? []) {
      const color = normalizeCellColor(row.color);
      if (color) colors.set(cellColorKey(row.field, row.row_key), color);
    }
    if ((data?.length ?? 0) < pageSize) break;
  }
  return colors;
}

/** Save a selected range in one transaction; null removes the fill. */
export async function saveCellColors(
  orgId: string,
  gridKey: string,
  changes: readonly CellColorChange[],
  documentId?: string | null,
): Promise<Error | null> {
  const payload = changes.map((change) => ({
    row_key: change.rowKey,
    field: change.field,
    color: normalizeCellColor(change.color),
  }));

  const { error } = await supabase.rpc('save_cell_colors', {
    p_org_id: orgId,
    p_grid_key: gridKey,
    p_colors: payload as unknown as Json,
    ...(documentId ? { p_document_id: documentId } : {}),
  });
  return error ?? null;
}
